import React, { useState, useEffect, useRef } from "react";
import { useLang } from "../context/LanguageContext";
import { t } from "../translations";

// Shared 44x44 hit-area for the pill's icon controls (HIG minimum).
const iconButton =
    "inline-flex items-center justify-center h-11 w-11 rounded-full text-label-secondary hover:text-label hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

export default function FloatingNav({ darkMode, setDarkMode }) {
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const { lang, toggleLang } = useLang();
    const tr = t[lang].nav;

    const navLinks = [
        { href: "#hero",                    label: tr.home },
        { href: "#about",                   label: tr.about },
        { href: "#skills",                  label: tr.skills },
        { href: "#projects",                label: tr.projects },
        { href: "#professional-experience", label: tr.experience },
        { href: "#contact",                 label: tr.contact },
    ];

    useEffect(() => {
        const handleScroll = () => {
            const heroSection = document.getElementById("hero");
            if (heroSection) {
                const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
                setShowScrollTop(window.scrollY > heroBottom);
            }
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Close mobile menu on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <>
            {/* ── Liquid Glass pill navbar — width hugs its content ── */}
            <div
                ref={menuRef}
                className="fixed left-1/2 -translate-x-1/2 top-5 z-50 flex flex-col items-center"
            >
                {/* Main pill */}
                <div className="glass rounded-full px-2 md:px-3 py-1.5 flex items-center gap-1">

                    {/* ── Desktop: nav links ── */}
                    <div className="hidden md:flex items-center gap-0.5 text-[13px] font-medium">
                        {navLinks.map(({ href, label }) => (
                            <a
                                key={href}
                                href={href}
                                className="whitespace-nowrap px-3 py-2 rounded-full transition-colors text-label-secondary hover:text-label hover:bg-black/[0.05] dark:hover:bg-white/[0.08]"
                            >
                                {label}
                            </a>
                        ))}
                    </div>

                    {/* ── Mobile: brand mark ── */}
                    <span className="md:hidden text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 select-none px-2">
                        HBM
                    </span>

                    {/* Divider — desktop only */}
                    <div className="hidden md:block w-px h-5 bg-separator mx-1" />

                    {/* Language toggle */}
                    <button
                        onClick={toggleLang}
                        className={`${iconButton} text-[11px] font-bold tracking-widest`}
                        aria-label="Toggle language"
                        title={lang === "en" ? "Passer en Français" : "Switch to English"}
                    >
                        {lang === "en" ? "FR" : "EN"}
                    </button>

                    {/* Dark mode toggle */}
                    <button
                        onClick={() => setDarkMode(!darkMode)}
                        className={iconButton}
                        aria-label="Toggle dark mode"
                    >
                        {darkMode ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        )}
                    </button>

                    {/* Hamburger — mobile only */}
                    <button
                        onClick={() => setMenuOpen((o) => !o)}
                        className={`md:hidden ${iconButton}`}
                        aria-label="Toggle menu"
                        aria-expanded={menuOpen}
                    >
                        {menuOpen ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* ── Mobile dropdown — appears below the pill ── */}
                <div
                    className={`md:hidden mt-2 glass rounded-3xl overflow-hidden transition-all duration-300 ${
                        menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                    <div className="flex flex-col py-2 px-2 min-w-[180px]">
                        {navLinks.map(({ href, label }) => (
                            <a
                                key={href}
                                href={href}
                                onClick={() => setMenuOpen(false)}
                                className="px-4 py-3 rounded-2xl text-[15px] font-medium text-label-secondary hover:text-label hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-colors whitespace-nowrap"
                            >
                                {label}
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Scroll-to-top button ── */}
            <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className={`fixed bottom-8 right-8 h-12 w-12 rounded-full bg-accent hover:bg-accent-hover text-white shadow-lg flex items-center justify-center transition-all duration-300 active:scale-95 z-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg ${
                    showScrollTop ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0 pointer-events-none"
                }`}
                aria-label="Scroll to top"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
            </button>
        </>
    );
}
